import { GButton } from 'components/basic-blocks/g-button'
import { useEffect, useMemo, useState } from 'react'
import { useStores } from 'hooks'
import { GLoading } from 'components/basic-blocks/g-loading'
import { AlertSquareIcon, AlertHexagonIcon } from 'assets/icons'
import Logo from '../../assets/img/cg-logo-update.svg'
import { AccountSwitcher } from 'layout/sidebar/menus/account-switcher'
import { ChatService } from 'services/chat'
import { useTrackPostHogEvent } from 'utils/posthog'
import { PostHogEvent, PostHogPath } from 'utils/posthog-events'
import { Impersonation } from 'layout/navbar/impersonation/impersonation'
import { useNavigate } from 'react-router-dom'
import { setItemsToLocalStorage } from 'utils'
import { ProductAccounts } from 'services/user'
import { parseError } from 'utils/errors'
import { PaymentMethodDTO, RetryOverdueResultDTO } from 'api-models'
import { PaymentMethodPicker } from './payment-method-picker'

type Phase = 'idle' | 'submitting' | 'verifying_3ds' | 'finalizing'

export const PaymentIssueDetected = () => {
	const navigate = useNavigate()
	const { accountStore, adminStore, assetStore, billingStore } = useStores()
	const trackPostHogEvent = useTrackPostHogEvent()
	const { account, setAccountForId, getAllAccounts } = accountStore
	const { allAssets } = assetStore

	const [phase, setPhase] = useState<Phase>('idle')
	const [error, setError] = useState<string | null>(null)
	const [selectedPmId, setSelectedPmId] = useState<string | undefined>(undefined)

	useEffect(() => {
		billingStore.getPaymentMethods()
	}, [])

	// Historical user-level PMs (no stripe_customer binding) remain visible to
	// keep the dropdown non-empty for older accounts.
	const paymentMethods = useMemo<PaymentMethodDTO[]>(() => {
		const all = billingStore.paymentMethods || []
		if (!account?.stripe?.stripe_customer_id) return all
		return all.filter((pm) => pm.account_id === account.id)
	}, [billingStore.paymentMethods, account?.id, account?.stripe?.stripe_customer_id])

	// Auto-select default PM (or first available) only when nothing is picked
	// yet, OR the current pick has been removed from the list.
	useEffect(() => {
		const currentStillValid = selectedPmId && paymentMethods.some((pm) => pm.id === selectedPmId)
		if (currentStillValid) return
		const defaultId = account?.billing?.payment_method?.id
		const onFile = defaultId ? paymentMethods.find((pm) => pm.id === defaultId) : undefined
		const next = onFile?.id ?? paymentMethods[0]?.id
		if (next && next !== selectedPmId) setSelectedPmId(next)
	}, [paymentMethods, account?.billing?.payment_method?.id, selectedPmId])

	useEffect(() => {
		if (trackPostHogEvent) {
			trackPostHogEvent(PostHogEvent.PAYMENT_DUE, PostHogPath.FAILED_PAYMENT, null)
		}
	}, [trackPostHogEvent])

	const isAdminImpersonating = adminStore?.impersonation === true

	const handleSkipToDashboard = () => {
		if (!isAdminImpersonating) return
		setItemsToLocalStorage([{ key: 'ad_bp_aze_imper_user', value: 'true' }])
		if (account?.id) {
			const workspaceAssets = allAssets.filter((a) => a.account_id === account.id)
			const assetId = workspaceAssets.length > 0 ? workspaceAssets[0].id : 'website-demo'
			navigate(`/workspace/${account.id}/asset/${assetId}`)
		}
	}

	if (!account) {
		return <GLoading />
	}

	const paymentDaysPassed = Math.round(
		account.billing?.subscription?.next_billing_date
			? Math.floor(
					new Date().getTime() - new Date(account.billing.subscription.next_billing_date).getTime()
			  ) /
					(1000 * 60 * 60 * 24)
			: 0
	)

	// Returns the post-3DS retry result on success; null if the customer
	// cancelled/failed the challenge (caller has already surfaced the error).
	const runThreeDSChallenge = async (
		clientSecret: string,
		paymentIntentId: string
	): Promise<RetryOverdueResultDTO | null> => {
		setPhase('verifying_3ds')
		const stripe = await billingStore.stripe
		if (!stripe) {
			setError('Payment service is unavailable. Please refresh the page and try again.')
			return null
		}
		const next = await stripe.handleNextAction({ clientSecret })
		if (next.error) {
			const rawMsg = next.error.message ?? 'Authentication failed'
			const looksCancelled = /cancel|abandon|dismiss/i.test(rawMsg)
			setError(looksCancelled ? 'Authentication was cancelled — please try again.' : rawMsg)
			trackPostHogEvent(PostHogEvent.RETRY_PAYMENT, PostHogPath.FAILED_PAYMENT, {
				status: 'failed',
				reason: 'authentication_failed'
			})
			return null
		}
		setPhase('finalizing')
		return ProductAccounts.retryOverduePayment(account.id, { payment_intent_id: paymentIntentId })
	}

	const handleSubmit = async () => {
		if (phase !== 'idle') return
		if (!selectedPmId) {
			setError('Please select a payment method.')
			return
		}
		setPhase('submitting')
		setError(null)

		try {
			let result = await ProductAccounts.retryOverduePayment(account.id, { pm_id: selectedPmId })

			if (result.kind === 'requires_action') {
				const post3ds = await runThreeDSChallenge(result.client_secret, result.payment_intent_id)
				if (!post3ds) return
				if (post3ds.kind !== 'succeeded') {
					setError('Unexpected response after authentication, please try again.')
					return
				}
				result = post3ds
			}

			// payment_overdue is cleared on the BE — refresh local state so the
			// main-page.tsx paywall gate unmounts.
			await getAllAccounts()
			await setAccountForId(account.id)
			trackPostHogEvent(PostHogEvent.RETRY_PAYMENT, PostHogPath.FAILED_PAYMENT, { status: 'success' })
		} catch (err: any) {
			const sanitized = parseError(err)
			const userMessage = sanitized || 'Something went wrong. Please try again.'
			setError(userMessage)
			trackPostHogEvent(PostHogEvent.RETRY_PAYMENT, PostHogPath.FAILED_PAYMENT, {
				status: 'failed',
				reason: sanitized || 'unknown'
			})
		} finally {
			setPhase('idle')
		}
	}

	const submitLabel = (() => {
		if (phase === 'submitting') return 'Processing...'
		if (phase === 'verifying_3ds') return 'Waiting for your bank...'
		if (phase === 'finalizing') return 'Finalizing...'
		return 'Retry payment now'
	})()

	const isBusy = phase !== 'idle'
	const isPaused = paymentDaysPassed > 7
	const daysUntilCanceled = 30 - paymentDaysPassed
	const daysUntilPause = 7 - paymentDaysPassed
	const iconColor = isPaused ? '#DE3024' : '#FABC04'

	return (
		<div className="bg-t-smoke min-h-screen md:bg-gradient-to-br from-g-purple via-g-cyan to-g-gray">
			<div className="flex flex-col min-h-[calc(100vh-72px)] ">
				<div className="flex pl-6 sm:pl-8 pt-6 sm:pt-8">
					<img className="w-40" src={Logo} alt="logo" />
				</div>
				<div className="transition-all duration-300 flex-shrink-0 flex h-[72px] bg-transparent z-20">
					<div className="w-full flex pl-6 sm:pl-8">
						<div className="flex-1 flex justify-between gap-4 mx-auto items-center relative">
							<div className="flex gap-8 divide-x-2 divide-select-field">
								<div className="flex items-center gap-4">
									<div>
										<AccountSwitcher type="dropdown" />
									</div>
								</div>
							</div>
						</div>
						<div className="flex flex-row gap-x-2">
							<div>
								<Impersonation
									footerContent={
										isAdminImpersonating && (
											<div className="pt-2">
												<GButton
													label="Skip to Dashboard"
													className="h-[38px] w-full rounded-full disabled:!shadow-none !shadow-s-yellow"
													onClick={handleSkipToDashboard}
												/>
											</div>
										)
									}
								/>
							</div>
						</div>
					</div>
				</div>
				<main className="flex-1 overflow-hidden pb-16 md:ml-20">
					<div className="relative max-w-[610px] flex md:gap-0 gap-12 md:items-center flex-col md:justify-center md:min-h-[80vh] mx-auto sm:px-6 lg:px-8 text-t-dark">
						<div className="md:py-6 w-full md:order-1 order-2">
							<div className="bg-t-smoke rounded-3xl md:shadow-lg overflow-hidden">
								<div className="lg:w-full lg:flex lg:flex-col space-y-4 relative sm:block md:p-12 sm:px-12 sm:py-12 p-2">
									<div className="mt-0 w-full relative">
										<div
											className={`${
												isPaused ? 'text-t-danger' : 'text-t-warning-700'
											} mb-8 md:text-left text-center md:text-[30px] text-2xl md:leading-9 leading-7 md:font-normal font-medium`}
										>
											{isPaused ? (
												<AlertHexagonIcon className="h-12 w-12" stroke={iconColor} />
											) : (
												<AlertSquareIcon className="h-12 w-12" stroke={iconColor} />
											)}
											Payment Issue Detected
											<div className="pt-2 text-t-desc-gray text-[16px] md:text-left text-center mt-1 font-[500] leading-5 w-full ">
												{isPaused
													? `Protection has been paused for all websites in the ${account.name} workspace.`
													: `Protection may pause soon for all websites in the ${account.name} workspace.`}
											</div>
										</div>
										<div className="gap-4 flex flex-col">
											<p>
												Your payment couldn&apos;t be processed — please update your payment method to
												ensure your campaigns stay protected from click fraud without interruption.
											</p>
											<p
												className={`${
													isPaused ? 'text-t-danger' : 'text-t-warning-700'
												} pt-4 text-[16px] md:text-left text-center font-[500] leading-5 w-full`}
											>
												{isPaused
													? `Your subscription will be canceled in ${daysUntilCanceled} days.`
													: `If not resolved, your protection will be paused in ${daysUntilPause} days.`}
											</p>

											<div className="space-y-4 mt-2">
												<PaymentMethodPicker
													paymentMethods={paymentMethods}
													selectedId={selectedPmId}
													onChange={(pm) => {
														setSelectedPmId(pm.id)
														setError(null)
													}}
													onAdded={(pm) => {
														setSelectedPmId(pm.id)
														setError(null)
													}}
													disabled={isBusy}
												/>
												{error && (
													<p className="text-sm text-red-500" role="alert">
														{error}
													</p>
												)}
												<GButton
													label={submitLabel}
													color="yellow"
													className="h-[38px] w-full rounded-full disabled:!shadow-none !shadow-s-yellow"
													labelClassName="font-bold"
													onClick={handleSubmit}
													loading={isBusy}
													disabled={isBusy || !selectedPmId}
												/>
											</div>

											<div className="flex items-center gap-1 justify-center mt-2">
												<p>Need help?</p>
												<button
													type="button"
													onClick={() => ChatService.open()}
													className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium"
												>
													Click here
												</button>
												<p>to contact support.</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</main>
			</div>
		</div>
	)
}
